"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDateRange } from "./DateRangeContext";
import CustomSelect from "../ui/CustomSelect";
import DropDownArrow from "../ui/DropDownArrow";
import { useAnalytics } from "@/hooks/useAnalytics";
import {
  months,
  weekdays,
  timeOptions,
  timeStringToHour,
  hourToTimeString,
  generateCalendarDays,
  isCurrentMonth,
  formatDate,
  formatDateMobile,
} from "./const";
import { SearchAnalytics, ModalAnalytics } from "@/lib/analyticsName";

interface DateRangePickerProps {
  className?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  className = "",
}) => {
  const {
    range,
    handleFromDateSelect,
    handleToDateSelect,
    handleTimeSelect,
    // handleClearAll: contextHandleClearAll,
    timeSlots,
    disabledDates,
  } = useDateRange();

  // Analytics tracking
  const { trackClick, createClickHandler } = useAnalytics();

  // Create date selection handler with analytics
  const createDateSelectionHandler = (date: Date, isFromDate: boolean) => {
    return createClickHandler(
      () => {
        if (isFromDate) {
          handleFromDateSelect(date);
        } else {
          handleToDateSelect(date);
        }
      },
      SearchAnalytics.DateSelect,
      SearchAnalytics.Category,
      {
        date_type: isFromDate ? 'from_date' : 'to_date',
        selected_date: date.toISOString(),
        current_from_date: fromDate?.toISOString(),
        current_to_date: toDate?.toISOString(),
        is_first_selection: isFirstSelection
      }
    );
  };

  // Create time selection handler with analytics
  const createTimeSelectionHandler = (time: string, isStartTime: boolean) => {
    return createClickHandler(
      () => handleTimeSelect(timeStringToHour(time), isStartTime),
      SearchAnalytics.TimeSelect,
      SearchAnalytics.Category,
      {
        time_type: isStartTime ? 'start_time' : 'end_time',
        selected_time: time,
        current_start_time: hourToTimeString(timeSlots.startTime),
        current_end_time: hourToTimeString(timeSlots.endTime)
      }
    );
  };

  // Create clear all handler with analytics
  // const createClearAllHandler = () => {
  //   return createClickHandler(
  //     () => contextHandleClearAll(),
  //     SearchAnalytics.ClearFilter,
  //     SearchAnalytics.Category,
  //     {
  //       action: 'clear_date_range',
  //       current_from_date: fromDate?.toISOString(),
  //       current_to_date: toDate?.toISOString(),
  //       current_start_time: hourToTimeString(timeSlots.startTime),
  //       current_end_time: hourToTimeString(timeSlots.endTime)
  //     }
  //   );
  // };

  // Create close handler with analytics
  const createCloseHandler = () => {
    return createClickHandler(
      () => setIsCalendarVisible(false),
      ModalAnalytics.Close,
      ModalAnalytics.Category,
      {
        modal_name: 'date_range_picker',
        action: 'close',
        final_from_date: fromDate?.toISOString(),
        final_to_date: toDate?.toISOString(),
        final_start_time: hourToTimeString(timeSlots.startTime),
        final_end_time: hourToTimeString(timeSlots.endTime)
      }
    );
  };

  // Set default dates: today and tomorrow
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Use context values instead of local state
  const fromDate = range.startDate;
  const toDate = range.endDate;

  const [currentMonth, setCurrentMonth] = useState(new Date()); // Current month
  const [isSelectingFrom, setIsSelectingFrom] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isFirstSelection, setIsFirstSelection] = useState(true);

  // Ref for the main container
  const mainContainerRef = useRef<HTMLDivElement>(null);

  // Set mounted state
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Effect to measure container width (only on client)
  useEffect(() => {
    if (!isMounted) return;

    const updateWidth = () => {
      if (mainContainerRef.current) {
        setContainerWidth(mainContainerRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateWidth);
    if (mainContainerRef.current) {
      resizeObserver.observe(mainContainerRef.current);
    }

    // Also listen to window resize
    window.addEventListener("resize", updateWidth);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [isMounted]);

  // Determine if we should show two calendars
  const showTwoCalendars = containerWidth > 600;

  // Memoized date checking functions
  const isSelected = useCallback((date: Date) => {
    if (!fromDate && !toDate) return false;

    const dateStr = date.toDateString();
    const fromStr = fromDate?.toDateString();
    const toStr = toDate?.toDateString();

    return dateStr === fromStr || dateStr === toStr;
  }, [fromDate, toDate]);

  const isInRange = useCallback((date: Date) => {
    if (!fromDate || !toDate) return false;
    return date > fromDate && date < toDate;
  }, [fromDate, toDate]);

  // After 11 PM, do not allow selecting today's date
  const isDisabled = useCallback((date: Date) => {
    if (disabledDates.some(
      (disabledDate) => disabledDate.toDateString() === date.toDateString()
    )) return true;
    const now = new Date();
    if (now.getHours() >= 23) {
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dateDayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (dateDayStart.getTime() === todayStart.getTime()) return true;
    }
    return false;
  }, [disabledDates]);

  // Handle close calendar
  const handleClose = createCloseHandler();

  // Handle clear all selections and reset to default values
  // const handleClearAll = createClearAllHandler();

  // Handle click outside to close calendar and ESC key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        mainContainerRef.current &&
        !mainContainerRef.current.contains(event.target as Node)
      ) {
        // Small delay to prevent immediate closing when opening
        setTimeout(() => {
          setIsCalendarVisible(false);
        }, 100);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsCalendarVisible(false);
      }
    };

    if (isCalendarVisible) {
      // Use a small delay to prevent immediate closing
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside as EventListener);
        document.addEventListener("touchstart", handleClickOutside as EventListener);
      }, 100);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside as EventListener);
        document.removeEventListener("touchstart", handleClickOutside as EventListener);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isCalendarVisible]);

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    if (isDisabled(date)) return;
    // First user selection always goes to TO date
    if (isFirstSelection) {
      createDateSelectionHandler(date, true)();
      setTimeout(() => {
        setIsSelectingFrom(false);
        setIsFirstSelection(false);
      }, 100);
    } else if (isSelectingFrom) {
      // Allow selecting from date even if it's greater than to date
      // The context will automatically update to date to from date + 1 day
      createDateSelectionHandler(date, true)();
    } else {
      // Selecting TO date (when switching back)
      createDateSelectionHandler(date, false)();
    }
  };

  // Handle time selection
  const handleTimeSelection = (timeStr: string, isStart: boolean) => {
    createTimeSelectionHandler(timeStr, isStart)();
  };

  const nextMonth = useCallback(() => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1)
    );
  }, []);

  const prevMonth = useCallback(() => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1)
    );
  }, []);

  const renderCalendar = (monthOffset = 0, additionalClasses = "") => {
    const calendarMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + monthOffset
    );
    const days = generateCalendarDays(calendarMonth);

    return (
      <div className={`flex-1 min-w-0 ${additionalClasses}`}>
        <div className="text-center mb-2 sm:mb-3">
          <h3 className="text-sm sm:text-base md:text-lg font-medium text-gray-600">
            {months[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
          </h3>
        </div>

        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2">
          {weekdays.map((day) => (
            <div
              key={day}
              className="text-center text-xs sm:text-sm font-medium text-gray-500 py-1 sm:py-2"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
          {days.map((date, index) => {
            const isCurrentMonthDate = isCurrentMonth(date, calendarMonth);
            const selected = isSelected(date);
            const inRange = isInRange(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const disabled = isDisabled(date);

            return (
              <button
                key={index}
                onClick={() =>
                  isCurrentMonthDate && !disabled && handleDateSelect(date)
                }
                className={`
                  h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 rounded-full text-xs sm:text-sm md:text-base font-medium transition-all duration-200 flex items-center justify-center
                  ${
                    !isCurrentMonthDate
                      ? "text-gray-300 cursor-not-allowed bg-transparent"
                      : ""
                  }
                  ${
                    disabled
                      ? "text-amber-700 cursor-not-allowed bg-gray-50"
                      : "hover:bg-orange-500"
                  }
                  ${selected ? "bg-orange-500 text-white" : ""}
                  ${inRange ? "bg-orange-200 text-orange-800" : ""}
                  ${isToday && !selected ? "border-2 border-blue-500" : ""}
                  ${
                    isCurrentMonthDate && !selected && !inRange && !disabled
                      ? "text-gray-700"
                      : ""
                  }
                `}
                disabled={!isCurrentMonthDate || disabled}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Don't render until mounted to prevent hydration mismatch
  if (!isMounted) {
    return (
      <div
        className={`relative w-full max-w-[1000px] mx-auto bg-white ${className}`}
      >
        <div className="flex justify-center flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-gray-600 font-medium text-sm sm:text-base">
              From
            </span>
            <div className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border-2 rounded-full font-medium text-sm sm:text-base border-gray-300 bg-gray-100 animate-pulse">
              <span className="text-gray-400">Loading...</span>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-gray-600 font-medium text-sm sm:text-base">
              To
            </span>
            <div className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border-2 rounded-full font-medium text-sm sm:text-base border-gray-300 bg-gray-100 animate-pulse">
              <span className="text-gray-400">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mainContainerRef}
      className={`relative w-full mx-auto bg-transparent md:bg-white ${className}`}
      style={{ position: 'relative' }}
    >
      {/* Date Range Header for greater lg */}
      <div className="hidden lg:flex justify-center flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-gray-600 font-medium text-sm sm:text-base">
            From
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsSelectingFrom(true);
              setIsCalendarVisible(!isCalendarVisible || !isSelectingFrom);
            }}
            className={`
              flex items-center gap-2 px-3 sm:px-4 py-2 border-2 rounded-full font-medium transition-all duration-200 text-sm sm:text-base
              ${
                isCalendarVisible && isSelectingFrom
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-300 hover:border-orange-300"
              }
            `}
          >
            <span className="hidden sm:inline whitespace-nowrap">
              {formatDate(
                fromDate,
                hourToTimeString(timeSlots.startTime),
                months
              ) || "Select date"}
            </span>
            <span className="sm:hidden whitespace-nowrap">
              {formatDateMobile(
                fromDate,
                hourToTimeString(timeSlots.startTime),
                months
              ) || "Select date"}
            </span>
            <DropDownArrow openDropdown={isCalendarVisible}/>
          </button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-gray-600 font-medium text-sm sm:text-base">
            To
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsSelectingFrom(false);
              setIsCalendarVisible(!isCalendarVisible || isSelectingFrom);
            }}
            className={`
              flex items-center justify-between gap-2 px-3 sm:px-4 py-2 border-2 rounded-full font-medium transition-all duration-200 text-sm sm:text-base
              ${
                isCalendarVisible && !isSelectingFrom
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-300 hover:border-orange-300"
              }
            `}
          >
            <span className="hidden sm:inline whitespace-nowrap">
              {formatDate(
                toDate,
                hourToTimeString(timeSlots.endTime),
                months
              ) || "Select date"}
            </span>
            <span className="sm:hidden whitespace-nowrap">
              {formatDateMobile(
                toDate,
                hourToTimeString(timeSlots.endTime),
                months
              ) || "Select date"}
            </span>
            <DropDownArrow openDropdown={isCalendarVisible}/>
          </button>
        </div>
      </div>
      {/* Date Range Header for greater md */}
      <div className="hidden md:flex lg:hidden justify-center flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center justify-between border border-gray-300 px-3 py-1 rounded-full gap-3 w-full sm:w-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsSelectingFrom(true);
              setIsCalendarVisible(!isCalendarVisible || !isSelectingFrom);
            }}
            className={`
              flex items-center justify-between gap-2 py-1 border-b-2  font-medium transition-all duration-200 text-sm
              ${
                isCalendarVisible && isSelectingFrom
                  ? "border-orange-500"
                  : "border-none"
              }
            `}
          >
            <span className="hidden sm:inline whitespace-nowrap">
              {formatDate(
                fromDate,
                hourToTimeString(timeSlots.startTime),
                months
              ) || "Select date"}
            </span>
            <span className="sm:hidden whitespace-nowrap">
              {formatDateMobile(
                fromDate,
                hourToTimeString(timeSlots.startTime),
                months
              ) || "Select date"}
            </span>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsSelectingFrom(false);
              setIsCalendarVisible(!isCalendarVisible || isSelectingFrom);
            }}
            className={`
              flex items-center justify-between gap-2 py-1 border-b-2 font-medium transition-all duration-200 text-sm
              ${
                isCalendarVisible && !isSelectingFrom
                  ? "border-orange-500"
                  : "border-none"
              }
            `}
          >
            <span className="hidden sm:inline whitespace-nowrap">
              {formatDate(
                toDate,
                hourToTimeString(timeSlots.endTime),
                months
              ) || "Select date"}
            </span>
            <span className="sm:hidden whitespace-nowrap">
              {formatDateMobile(
                toDate,
                hourToTimeString(timeSlots.endTime),
                months
              ) || "Select date"}
            </span>
            
          </button>
          <DropDownArrow openDropdown={isCalendarVisible}/>
        </div>
      </div>
      {/* Date Range Header for greater mobile */}
      <div className="flex md:hidden justify-center flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 ">
        <button
          type="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // Track analytics for date box click
            trackClick('Date_Range_Mobile_Box', 'Date_Selection', {
              action: 'open_calendar',
              time_slot_start: timeSlots.startTime,
              time_slot_end: timeSlots.endTime,
            });
            
            // Toggle calendar visibility
            setIsCalendarVisible(!isCalendarVisible);
            // If opening, default to selecting "from" date
            if (!isCalendarVisible) {
              setIsSelectingFrom(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsCalendarVisible(!isCalendarVisible);
              if (!isCalendarVisible) setIsSelectingFrom(true);
            }
          }}
          className="flex items-center justify-around border border-gray-300 px-4 sm:px-3 py-1 md:py-2 rounded-full gap-1 sm:gap-4 min-w-0 w-full sm:w-auto bg-white cursor-pointer hover:border-orange-300 transition-colors touch-manipulation text-left min-h-[44px]"
        >
          <div className="flex items-center gap-0.5 sm:gap-1 py-1 min-w-0">
            <span className="text-[#F05A24] font-semibold text-[12px] sm:text-sm flex-shrink-0">
              From:
            </span>
            <span className="hidden sm:inline text-base font-medium text-gray-700 min-w-0 whitespace-nowrap">
              {formatDate(
                fromDate,
                hourToTimeString(timeSlots.startTime),
                months
              ) || "Select date"}
            </span>
            <span className="sm:hidden text-[12px] font-medium text-gray-700 min-w-0 whitespace-nowrap">
              {formatDateMobile(
                fromDate,
                hourToTimeString(timeSlots.startTime),
                months
              ) || "Select date"}
            </span>
            <span className="hidden min-[390px]:inline sm:hidden flex-shrink-0"><DropDownArrow openDropdown={isCalendarVisible} iconSize="w-4 h-4"/></span>
          </div>

          <div className="flex items-center justify-end gap-0.5 sm:gap-1 py-1 flex-1 min-w-0 overflow-hidden flex-shrink">
            <span className="text-[#F05A24] font-semibold text-[12px] sm:text-sm flex-shrink-0">
              To:
            </span>
            <span className="hidden sm:inline text-base font-medium text-gray-700 min-w-0 whitespace-nowrap">
              {formatDate(
                toDate,
                hourToTimeString(timeSlots.endTime),
                months
              ) || "Select date"}
            </span>
            <span className="sm:hidden text-[12px] font-medium text-gray-700 min-w-0 whitespace-nowrap">
              {formatDateMobile(
                toDate,
                hourToTimeString(timeSlots.endTime),
                months
              ) || "Select date"}
            </span>
          </div>
          
          <span className="flex-shrink-0"><DropDownArrow openDropdown={isCalendarVisible} iconSize="w-4 h-4 sm:w-5 sm:h-5"/></span>
        </button>
      </div>

      {/* Calendar Container */}
      {isCalendarVisible && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 mt-2 w-[calc(100vw-20px)] md:w-full max-w-[600px] md:max-w-none z-[100]">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 md:p-4 overflow-visible">
            <div>
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-2 md:mb-4 gap-1 sm:gap-2">
                <button
                  onClick={prevMonth}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                </button>

                <div className="flex items-center gap-1 sm:gap-2 md:gap-4 flex-1 justify-center">
                  <CustomSelect
                    value={currentMonth.getMonth().toString()}
                    options={months.map((month, index) => ({
                      value: index.toString(),
                      label: month,
                    }))}
                    onChange={(value) =>
                      setCurrentMonth(
                        new Date(currentMonth.getFullYear(), parseInt(value))
                      )
                    }
                    placeholder="Select month"
                    width="120px"
                    height="28px"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                    }}
                    textSize="12px"
                    mobileColumns={1}
                    desktopColumns={1}
                    maxHeight="200px"
                    iconOpacity={0.5}
                    iconSize="w-4 h-4"
                  />

                  <CustomSelect
                    value={currentMonth.getFullYear().toString()}
                    options={Array.from(
                      { length: 20 },
                      (_, i) => new Date().getFullYear() - 0 + i
                    ).map((year) => ({
                      value: year.toString(),
                      label: year.toString(),
                    }))}
                    onChange={(value) =>
                      setCurrentMonth(
                        new Date(parseInt(value), currentMonth.getMonth())
                      )
                    }
                    placeholder="Select year"
                    width="100px"
                    height="28px"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                    }}
                    textSize="12px"
                    mobileColumns={1}
                    desktopColumns={1}
                    maxHeight="200px"
                    iconOpacity={0.5}
                    iconSize="w-4 h-4"
                  />
                </div>

                <button
                  onClick={nextMonth}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                </button>
              </div>

              {/* Calendar Grid - Based on container width */}
              <div
                className={`flex ${
                  showTwoCalendars ? "flex-row gap-4 md:gap-8" : "flex-col gap-4"
                }`}
              >
                {/* First month - always visible */}
                {renderCalendar(0)}

                {/* Second month - only visible when container width > 500px */}
                {showTwoCalendars && (
                  <div className="flex-1">{renderCalendar(1)}</div>
                )}
              </div>
            </div>
            
            {/* Time Selection */}
            <div className="mt-4 sm:mt-6 mb-3 sm:mb-4 space-y-3 sm:space-y-4 border-t border-gray-200 pt-3 sm:pt-4 relative z-[101] overflow-visible">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="relative z-[101] overflow-visible">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <CustomSelect
                    value={hourToTimeString(timeSlots.startTime)}
                    options={timeOptions.map((time) => ({
                      value: time,
                      label: time,
                    }))}
                    onChange={(value) => handleTimeSelection(value, true)}
                    placeholder="Select start time"
                    width="100%"
                    height="40px"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                    }}
                    textSize="14px"
                    mobileColumns={1}
                    desktopColumns={2}
                    maxHeight="250px"
                  />
                </div>

                <div className="relative z-[101] overflow-visible">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time
                  </label>
                  <CustomSelect
                    value={hourToTimeString(timeSlots.endTime)}
                    options={timeOptions.map((time) => ({
                      value: time,
                      label: time,
                    }))}
                    onChange={(value) => handleTimeSelection(value, false)}
                    placeholder="Select end time"
                    width="100%"
                    height="40px"
                    disabled={!toDate}
                    style={{
                      backgroundColor: !toDate ? "#f3f4f6" : "#fff",
                      border: "1px solid #e5e7eb",
                    }}
                    textSize="14px"
                    mobileColumns={1}
                    desktopColumns={2}
                    maxHeight="250px"
                  />
                </div>
              </div>
            </div>
            
            {/* Bottom Action Buttons */}
            <div className="flex justify-end gap-2 sm:gap-3">
              {/* <button
                onClick={handleClearAll}
                className="w-fit px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-full transition-colors duration-200"
              >
                Reset to Default
              </button> */}
              <button
                onClick={handleClose}
                className="w-fit px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm sm:text-base font-medium rounded-full transition-colors duration-200"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
